import { Inject, Injectable } from "@angular/core";
import { HttpClient, HttpHeaders } from "@angular/common/http";
import { BehaviorSubject, Observable, lastValueFrom, of } from "rxjs";
import { catchError, finalize, map, tap } from "rxjs/operators";
import { User } from "src/app/core/models/user";
import { RegisterUser } from "../models/register";
import { MatSnackBar } from "@angular/material/snack-bar";
import { Router } from "@angular/router";
import { EnvironmentService } from "./environment.service";
import { MsalService } from "@azure/msal-angular";
import OktaAuth from "@okta/okta-auth-js";
import { OKTA_AUTH } from "@okta/okta-angular";
import { AuthType } from "../models/globals";
import { NavigationService } from "./navigation.service";
import { AuthConfigs } from "../models/authenticationConfig";
import { UserRoleNames } from "../models/users";
import { ConcurrentUsersService } from "./concurrent-users.service";
import { AuthService } from "@auth0/auth0-angular";
import { OAuthService } from "angular-oauth2-oidc";

@Injectable({ providedIn: "root" })
export class AuthenticationService {
  public readonly SyncHPCVersion = "Version 2025.Q3.10.2333";

  private currentUserSubject: BehaviorSubject<User>;
  public currentUser: Observable<User>;

  constructor(
    private http: HttpClient,
    private snackbar: MatSnackBar,
    private router: Router,
    private env: EnvironmentService,
    private concurrentUsersService: ConcurrentUsersService,
    @Inject(OKTA_AUTH) private oktaAuth: OktaAuth,
    private msalService: MsalService,
    private navService: NavigationService,
    private openIdAuth: AuthService,
    private oauthService: OAuthService
  ) {
    this.currentUserSubject = new BehaviorSubject<User>(
      JSON.parse(localStorage.getItem("currentUser"))
    );
    this.currentUser = this.currentUserSubject.asObservable();
  }

  get loggedInShortUserName(): string {
    return this.currentUserSubject.value?.username?.split("@")[0];
  }

  public get currentUserValue(): User {
    return this.currentUserSubject.value;
  }

  logInPageRedirect: boolean | false = false;
  isDefaultAdminLogin: boolean | false = false;


  getCurrentSessionId(): string | null {
    try {
      const currentUser = localStorage.getItem('currentUser');
      if (currentUser) {
        const user = JSON.parse(currentUser);
        return user.sessionId;
      }
      return "";
    } catch (error) {
      return "";
    }
  }

  updateCurrentUserValue(data: User) {
    this.currentUserSubject.next(data);
  }

  isUserVdiOnly(userName: string) {
    let baseUrl =
      this.env.envVariables.httpProtocol +
      this.env.envVariables.webHost +
      "UserInfoes/VdiUserCheck/" +
      userName;
    let headers = new HttpHeaders();
    headers = headers
      .set("Content-Type", "application/json; charset=utf-8")
      .set("Cache-Control", "no-cache");

    return this.http.get<any>(baseUrl);
  }


  login(username: string, password: string, adminLogin: boolean = false) {
    let baseUrl =
      this.env.envVariables.httpProtocol +
      this.env.envVariables.webHost +
      "UserInfoes/authenticate";
    let adminLoginParam = adminLogin ? "true" : "false";
    let headers = new HttpHeaders();
    headers = headers.set("isadminlogin", adminLoginParam);

    if (adminLogin) {
      localStorage.setItem("isadminlogin", "true");
    }

    return this.http
      .post<any>(baseUrl, { username, password }, { headers: headers })
      .pipe(
        map((user) => {
          // store user details and jwt token in local storage to keep user logged in between page refreshes
          localStorage.setItem("currentUser", JSON.stringify(user));
          if (user?.isAdminSession) {
            localStorage.setItem("isadminsession", "true");
          }
          localStorage.removeItem("isadminlogin");
          this.currentUserSubject.next(user);
          return user;
        })
      );
  }

  //  add new  parameter isDesktopApp
  async logout(redirectClient: boolean = false, isDestopApp: boolean = false) {
    // remove user from local storage to log user out
    let currUser = this.getLoggedInUserAndIdFromLocalStorage();
    let isAdminSession = localStorage.getItem("isadminsession");
    let isadminsession = isAdminSession == "true" ? true : false;
    this.concurrentUsersService
      .remove(currUser, true)
      .pipe(
        finalize(async () => {
          // localStorage.clear();
          if (isadminsession === false) {
            if (this.getAuthenticationType() == AuthType.Okta) {
              await this.oktaAuth.signOut();
            } else if (this.getAuthenticationType() == AuthType.PingIdentity) {
              this.oauthService.logOut(true);
            } else if (this.getAuthenticationType() == AuthType.AzureAD) {
              await this.msalService.logout();
            } else if (this.getAuthenticationType() == AuthType.OpenId) {
              const data = await lastValueFrom(this.expireUserTokenOnLogout());
            }
          }
          localStorage.removeItem("lastVisitedRoute");
          localStorage.clear();
          this.currentUserSubject.next(null);
          this.closeLicenseExpireSnackbar();
          this.navService.closeOpenedDialogs();
          if (redirectClient) {
            // check if its a desktop app
            // if true redirect to destopapp login
            // if false redirect to webapp login
            if (isDestopApp) {
              this.router
                .navigate(["Client/desktopapp/Login"], {
                  queryParams: { user: currUser },
                })
                .then(() => {
                  window.location.reload();
                });
            } else {
              this.router
                .navigate(["Client/webapp/Login"], {
                  queryParams: { user: currUser },
                })
                .then(() => {
                  window.location.reload();
                });
            }
          } else {
            this.router.navigate([""]).then(() => {
              window.location.reload();
            });
            //window.location.reload()
          }
        })
      )
      .subscribe({
        next: async (data) => { },
        error: (err) => { },
      });
  }

  loggedIn() {
    //returns boolean
    return !!localStorage.getItem("currentUser");
  }

  getRole() {
    //for getting the role of logged in user
    let role: string = localStorage.getItem("currentUser")
      ? JSON.parse(localStorage.getItem("currentUser")).role
      : null;
    if (role == "") role = null;
    return role;
  }

  get isUserGlobalAdmin(): boolean {
    if (this.getRole() == UserRoleNames.GlobalAdmin) return true;
    return false;
  }

  isLoggedInUserEqualToRequestor(userId: string): boolean {
    return this.currentUserValue.username === userId;
  }

  checkIfUserRegistered() {
    let baseUrl =
      this.env.envVariables.httpProtocol +
      this.env.envVariables.webHost +
      "UserInfoes/isUserRegistered";
    return this.http.get<any>(baseUrl);
  }

  registerNewUser(newUserData: RegisterUser) {
    let baseUrl =
      this.env.envVariables.httpProtocol +
      this.env.envVariables.webHost +
      "UserInfoes/registerNewUser";
    const userInfo = JSON.stringify(newUserData);
    let headers = new HttpHeaders();
    headers = headers
      .set("Content-Type", "application/json; charset=utf-8")
      .set("Cache-Control", "no-cache");
    return this.http.post<any>(baseUrl, userInfo, {
      headers: headers,
    });
  }

  showLicenseExpireSnackbar(msg: string) {
    this.snackbar.open(msg);
  }

  closeLicenseExpireSnackbar() {
    this.snackbar.dismiss();
  }

  async checkUserDeploymentAccess(depName) {
    return new Promise((resolve, reject) => {
      let baseUrl =
        this.env.envVariables.httpProtocol +
        this.env.envVariables.webHost +
        "UserInfoes/CheckUserAccess/" +
        depName;
      let headers = new HttpHeaders();
      headers = headers
        .set("Content-Type", "application/json; charset=utf-8")
        .set("Cache-Control", "no-cache");
      this.http.get(baseUrl, { headers: headers }).subscribe({
        next: (data: any) => {
          resolve(data.userRole);
        },
        error: (err) => {
          this.snackbar.open(err.error.actionMessage, "Ok", {
            duration: 5000,
          });
          this.router.navigate(["/"]);
          resolve(err.error.userRole);
        },
      });
    });
  }

  getLoggedInUserFromLocalStorage() {
    let currentUser = JSON.parse(localStorage.getItem("currentUser"));
    if (!currentUser) return null;
    return currentUser.username;
  }

  getLoggedInUserAndIdFromLocalStorage() {
    let currentUser = JSON.parse(localStorage.getItem("currentUser"));
    if (!currentUser) return null;
    return { userId: currentUser.username, id: currentUser.sessionId };
  }

  getAuthenticationType() {
    //for getting the authentication type
    let authenticationType: number = Number(
      localStorage.getItem("authenticationType")
    )
      ? Number(JSON.parse(localStorage.getItem("authenticationType")))
      : 0;
    return authenticationType;
  }

  getAuthenticationValues() {
    let baseUrl =
      this.env.envVariables.httpProtocol +
      this.env.envVariables.webHost +
      "UserInfoes/GetAuthenticationType";
    let headers = new HttpHeaders();
    headers = headers
      .set("Content-Type", "application/json; charset=utf-8")
      .set("Cache-Control", "no-cache")
      .set("FromMain", "false");

    return this.http.get(baseUrl, { headers: headers });
  }

  updateAuthenticationConfig(authConfigs: AuthConfigs) {
    let baseUrl =
      this.env.envVariables.httpProtocol +
      this.env.envVariables.webHost +
      "UserInfoes/UpdateAuthenticationConfig";
    let headers = new HttpHeaders();
    const authConfigsInfo = JSON.stringify(authConfigs);

    headers = headers
      .set("Content-Type", "application/json; charset=utf-8")
      .set("Cache-Control", "no-cache");

    return this.http.post(baseUrl, authConfigsInfo, { headers: headers });
  }

  getTimeZone() {
    let baseUrl =
      this.env.envVariables.httpProtocol +
      this.env.envVariables.webHost +
      "UserInfoes/TimeZone";
    return this.http.get<any>(baseUrl);
  }

  getUserInactivityLogoutTime() {
    let baseUrl =
      this.env.envVariables.httpProtocol +
      this.env.envVariables.webHost +
      "ProfileInfoes/GetUserInactivityLogoutTime";
    return this.http.get<any>(baseUrl);
  }

  setLogInUiConfiguration(newData) {
    let baseUrl =
      this.env.envVariables.httpProtocol +
      this.env.envVariables.webHost +
      "UserInfoes/UpdateLogInUiConfig";
    let headers = new HttpHeaders();
    const logInUiConfigs = JSON.stringify(newData);

    headers = headers
      .set("Content-Type", "application/json; charset=utf-8")
      .set("Cache-Control", "no-cache");

    return this.http.post(baseUrl, logInUiConfigs, { headers: headers });
  }

  expireUserTokenOnLogout(): Observable<any> {
    let baseUrl =
      this.env.envVariables.httpProtocol +
      this.env.envVariables.webHost +
      "UserInfoes/ExpireUserTokenOnLogout";
    let headers = new HttpHeaders();
    headers = headers
      .set("Content-Type", "application/json; charset=utf-8")
      .set("Cache-Control", "no-cache");
    return this.http.get<any>(baseUrl, { headers: headers }).pipe(
      tap(
        (data) => {
          this.openIdAuth.logout();
          return of(true);
        },
        catchError((error) => {
          console.error(error);
          return of(false);
        })
      )
    );
  }

  samlLogin() {
    let baseUrl =
      this.env.envVariables.httpProtocol +
      this.env.envVariables.webHost +
      "Auth/SAML/Login";
    let headers = new HttpHeaders();
    headers = headers
      .set("Content-Type", "application/json; charset=utf-8")
      .set("Cache-Control", "no-cache");

    return this.http.post(baseUrl, { headers: headers });
  }

  samlLogout() {
    let baseUrl =
      this.env.envVariables.httpProtocol +
      this.env.envVariables.webHost +
      "Auth/SAML/SingleLogout";
    let headers = new HttpHeaders();
    headers = headers
      .set("Content-Type", "application/json; charset=utf-8")
      .set("Cache-Control", "no-cache");

    return this.http.post(baseUrl, { headers: headers });
  }
}
